import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from '@app/app.module';
import { initRendererConsoleInterceptor } from '@app/core/utils/renderer-console-interceptor';
import { GuestAppModule } from '@app/guest-app.module';
import { environment } from '@environment/environment';
import * as Sentry from '@sentry/angular';

// Initialize console interceptor early to capture all logs with timestamps
initRendererConsoleInterceptor();

if (environment.production) {
  enableProdMode();
}

/**
 * Determine which application module to bootstrap based on the current URL.
 * Guest meeting routes get the minimal GuestAppModule without auth services.
 * All other routes get the full AppModule with authentication.
 */
function getBootstrapModule() {
  const currentPath = window.location.pathname;
  const isGuestMeetingRoute = /^\/meeting\/[^\/]+\/join/.test(currentPath);

  if (isGuestMeetingRoute) {
    console.log('[BOOTSTRAP] Guest meeting route detected, bootstrapping GuestAppModule');
    return GuestAppModule;
  } else {
    console.log('[BOOTSTRAP] Standard route detected, bootstrapping AppModule');
    return AppModule;
  }
}

// Only initialize Sentry for the main application (not for guests)
const moduleToBootstrap = getBootstrapModule();
if (moduleToBootstrap === AppModule) {
  Sentry.init({
    environment: environment.name,
    dsn: environment.sentryDSN,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration(),
      // Registers the Replay integration,
      // which automatically captures Session Replays
      Sentry.replayIntegration(),
    ],
    release: environment.awsCommitId,
    debug: false,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: environment.sentrySampleRate,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['https://beta.connectuc.io/', 'https://app.connectuc.io/'],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
  });
}

platformBrowserDynamic()
  .bootstrapModule(moduleToBootstrap)
  .catch((error) => console.error(`[BOOTSTRAP] Failed to bootstrap application: ${error}`));

// Service worker registration is handled by Angular's ServiceWorkerModule in app.module.ts
// It will automatically skip registration when running from file:// protocol (Electron)
if (window.location.protocol === 'file:') {
  console.log('Running in Electron (file:// protocol) - Service Worker registration will be skipped');
}
